/**
 * tierInheritance.js — the UI half of SOUND-795.
 *
 * CPO rule (2026-11-03): a child of a Managed Services parent DEFAULTS to
 * Managed Services. It is a default, not a constraint — an admin can still
 * pick another tier — and only Managed Services inherits.
 *
 * sound-api enforces the rule (create, link, and a parent moving TO Managed
 * Services cascades to its children and reports `childrenRetiered`). This
 * module only decides what the UI pre-fills and what it tells the admin.
 *
 * PURE. No React, no network — sound-app has no component-test harness
 * (SOUND-679), so the decisions live here where they can be asserted.
 */

import { MANAGED_SERVICES_TIER } from "../constants/companies";

/**
 * The tier a child inherits from this parent, or null when it inherits
 * nothing.
 *
 * @param {{ tier?: string } | null | undefined} parent
 * @returns {string | null}
 */
export function inheritedTierFrom(parent) {
  return parent?.tier === MANAGED_SERVICES_TIER ? MANAGED_SERVICES_TIER : null;
}

/**
 * What the create form's tier should be after the parent selection changes.
 *
 *  - Parent inherits a tier AND the admin has not picked one → pre-select it,
 *    and flag it as defaulted (drives the hint under the field).
 *  - The admin already chose a tier → never overwrite it.
 *  - Parent cleared or non-inheriting → leave the tier alone (nothing is
 *    forced back), but it is no longer "defaulted from parent", so the hint
 *    goes away.
 *
 * @param {{ tier: string, parent: object | null, tierDefaulted: boolean }} args
 * @returns {{ tier: string, tierDefaulted: boolean }}
 */
export function tierAfterParentChange({ tier, parent, tierDefaulted }) {
  const current = String(tier || "").trim();
  const inherited = inheritedTierFrom(parent);

  if (!inherited) return { tier: current, tierDefaulted: false };

  // Blank, or still holding the value WE defaulted from a previous parent.
  if (!current || (tierDefaulted && current === inherited)) {
    return { tier: inherited, tierDefaulted: true };
  }
  return { tier: current, tierDefaulted: false };
}

/**
 * Hint text under the tier field, or null.
 *
 * @param {{ tierDefaulted: boolean, tier: string }} args
 * @returns {string | null}
 */
export function tierDefaultHint({ tierDefaulted, tier }) {
  return tierDefaulted && tier ? `Defaulted from parent (${tier})` : null;
}

/**
 * `childrenRetiered` from a `PUT /companies/:id` response, as a safe count.
 * A missing or malformed field is 0, so the UI works before the API ships.
 *
 * @param {object | null | undefined} body - response body
 * @returns {number}
 */
export function childrenRetieredCount(body) {
  const n = Number(body?.childrenRetiered);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

/**
 * Toast description for a parent's tier save that re-tiered its children,
 * or null when none moved.
 *
 * @param {number} count
 * @returns {string | null}
 */
export function childrenRetieredMessage(count) {
  if (!Number.isInteger(count) || count <= 0) return null;
  const noun = count === 1 ? "child company" : "child companies";
  return `Also updated ${count} ${noun} to ${MANAGED_SERVICES_TIER}.`;
}

/**
 * After linking a child to a parent: a sentence describing the tier change
 * the API ACTUALLY made, read from the response — never predicted, so it is
 * silent before the API ships and when nothing moved.
 *
 * @param {{ beforeTier?: string, after?: { tier?: string } | null }} args
 * @returns {string | null}
 */
export function linkTierChangeMessage({ beforeTier, after }) {
  const next = after?.tier;
  if (!next || next === (beforeTier || "")) return null;
  return `Tier set to ${next}, inherited from the parent.`;
}
